/** Vouchers (промокоды) — full CRUD + multi-code generator (Phase D2). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Plus,
  Sparkles,
  Ticket,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

type DiscountType = "VALUE_FIXED" | "VALUE_PERCENTAGE" | "SHIPPING";

const DISCOUNT_LABEL: Record<DiscountType, string> = {
  VALUE_FIXED: "Фикс. сумма (тийины)",
  VALUE_PERCENTAGE: "Процент",
  SHIPPING: "Бесплатная доставка",
};

interface VoucherCode {
  id: number;
  code: string;
  used_count: number;
  is_active: boolean;
  created_at: string;
}

interface Voucher {
  id: number;
  name: string;
  description: string | null;
  discount_type: DiscountType;
  discount_value: number;
  min_spend_tiyin: number | null;
  min_quantity: number | null;
  usage_limit_total: number | null;
  usage_limit_per_customer: number | null;
  once_per_order: boolean;
  staff_only: boolean;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  codes: VoucherCode[];
}

type FormState = {
  name: string;
  description: string;
  discount_type: DiscountType;
  discount_value: string;
  min_spend_tiyin: string;
  min_quantity: string;
  usage_limit_total: string;
  usage_limit_per_customer: string;
  once_per_order: boolean;
  staff_only: boolean;
  is_active: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  discount_type: "VALUE_PERCENTAGE",
  discount_value: "10",
  min_spend_tiyin: "",
  min_quantity: "",
  usage_limit_total: "",
  usage_limit_per_customer: "1",
  once_per_order: true,
  staff_only: false,
  is_active: true,
};

function toForm(v: Voucher): FormState {
  return {
    name: v.name,
    description: v.description ?? "",
    discount_type: v.discount_type,
    discount_value: String(v.discount_value),
    min_spend_tiyin: v.min_spend_tiyin != null ? String(v.min_spend_tiyin) : "",
    min_quantity: v.min_quantity != null ? String(v.min_quantity) : "",
    usage_limit_total:
      v.usage_limit_total != null ? String(v.usage_limit_total) : "",
    usage_limit_per_customer:
      v.usage_limit_per_customer != null
        ? String(v.usage_limit_per_customer)
        : "",
    once_per_order: v.once_per_order,
    staff_only: v.staff_only,
    is_active: v.is_active,
  };
}

function buildPayload(form: FormState): Record<string, unknown> {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    discount_type: form.discount_type,
    discount_value: parseFloat(form.discount_value),
    min_spend_tiyin: form.min_spend_tiyin
      ? parseInt(form.min_spend_tiyin, 10)
      : null,
    min_quantity: form.min_quantity ? parseInt(form.min_quantity, 10) : null,
    usage_limit_total: form.usage_limit_total
      ? parseInt(form.usage_limit_total, 10)
      : null,
    usage_limit_per_customer: form.usage_limit_per_customer
      ? parseInt(form.usage_limit_per_customer, 10)
      : null,
    once_per_order: form.once_per_order,
    staff_only: form.staff_only,
    is_active: form.is_active,
  };
}

export function VouchersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [generatorVoucher, setGeneratorVoucher] = useState<Voucher | null>(null);

  const { data: vouchers, isLoading } = useQuery({
    queryKey: ["vouchers"],
    queryFn: async () => {
      const { data } = await api.get("/vouchers");
      return data as Voucher[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/vouchers/${id}`);
    },
    onSuccess: () => {
      toast.success("Voucher удалён");
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить voucher?",
  });

  const deleteCodeMutation = useMutation({
    mutationFn: async (codeId: number) => {
      await api.delete(`/vouchers/codes/${codeId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["vouchers"] }),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <Ticket className="w-6 h-6" />
            Vouchers (промокоды)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Multiple codes per voucher + лимиты для SMS/TG-кампаний (Phase D2).
            Применение кодов в корзине — в следующей итерации (нужен cart engine).
          </p>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать
        </Button>
      </div>

      {editingId === "new" && (
        <VoucherForm
          mode="create"
          initial={EMPTY_FORM}
          onClose={() => setEditingId(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {vouchers?.length === 0 && editingId !== "new" && (
          <Card className="lg:col-span-2">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Vouchers нет. Создайте первый, потом сгенерируйте коды.
            </CardContent>
          </Card>
        )}
        {vouchers?.map((v) => (
          <Card key={v.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {v.name}
                    {!v.is_active && (
                      <Badge variant="secondary" className="text-[10px]">
                        off
                      </Badge>
                    )}
                  </CardTitle>
                  {v.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {v.description}
                    </p>
                  )}
                </div>
                <Badge variant="outline">
                  {DISCOUNT_LABEL[v.discount_type]} · {String(v.discount_value)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                {v.min_spend_tiyin != null && (
                  <span>min spend: {Number(v.min_spend_tiyin) / 100} сум</span>
                )}
                {v.usage_limit_total != null && (
                  <span>лимит: {v.usage_limit_total}</span>
                )}
                {v.usage_limit_per_customer != null && (
                  <span>per-customer: {v.usage_limit_per_customer}</span>
                )}
                {v.staff_only && <Badge variant="secondary">staff only</Badge>}
              </div>

              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Codes ({v.codes.length})
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGeneratorVoucher(v)}
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    Generate
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                  {v.codes.length === 0 ? (
                    <span className="text-xs text-muted-foreground italic">
                      Кодов нет
                    </span>
                  ) : (
                    v.codes.map((c) => (
                      <Badge
                        key={c.id}
                        variant="outline"
                        className="font-mono text-[11px] gap-1 pr-1"
                      >
                        {c.code}
                        {c.used_count > 0 && (
                          <span className="text-muted-foreground">
                            ·{c.used_count}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteCodeMutation.mutate(c.id)}
                          className="text-muted-foreground hover:text-destructive ml-1"
                        >
                          ×
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>

              {editingId === v.id ? (
                <VoucherForm
                  mode="edit"
                  voucher={v}
                  initial={toForm(v)}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(v.id)}
                  >
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(v.id, v.name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {generatorVoucher && (
        <GeneratorDialog
          voucher={generatorVoucher}
          onClose={() => setGeneratorVoucher(null)}
        />
      )}
      {deleteDialog}
    </div>
  );
}

function VoucherForm({
  mode,
  voucher,
  initial,
  onClose,
}: {
  mode: "create" | "edit";
  voucher?: Voucher;
  initial: FormState;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(initial);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      if (mode === "create") {
        await api.post("/vouchers", payload);
      } else if (voucher) {
        await api.patch(`/vouchers/${voucher.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Создан" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  return (
    <Card className={mode === "edit" ? "mt-3 border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новый voucher</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Название *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Новогодняя акция 2026"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Описание (для админки)</Label>
            <Textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={2}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Тип скидки</Label>
            <Select
              value={form.discount_type}
              onValueChange={(v: DiscountType) =>
                setForm({ ...form, discount_type: v })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DISCOUNT_LABEL).map(([k, l]) => (
                  <SelectItem key={k} value={k}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Размер скидки</Label>
            <Input
              type="number"
              step="0.01"
              value={form.discount_value}
              onChange={(e) =>
                setForm({ ...form, discount_value: e.target.value })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Min spend (тийины)</Label>
            <Input
              type="number"
              value={form.min_spend_tiyin}
              onChange={(e) =>
                setForm({ ...form, min_spend_tiyin: e.target.value })
              }
              placeholder="например 5000000 = 50000 сум"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Min quantity</Label>
            <Input
              type="number"
              value={form.min_quantity}
              onChange={(e) =>
                setForm({ ...form, min_quantity: e.target.value })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Лимит total</Label>
            <Input
              type="number"
              value={form.usage_limit_total}
              onChange={(e) =>
                setForm({ ...form, usage_limit_total: e.target.value })
              }
              placeholder="без лимита"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Лимит per-customer</Label>
            <Input
              type="number"
              value={form.usage_limit_per_customer}
              onChange={(e) =>
                setForm({ ...form, usage_limit_per_customer: e.target.value })
              }
            />
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap pt-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.once_per_order}
              onChange={(e) =>
                setForm({
                  ...form,
                  once_per_order: (e.target as HTMLInputElement).checked,
                })
              }
            />
            Один на заказ
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.staff_only}
              onChange={(e) =>
                setForm({
                  ...form,
                  staff_only: (e.target as HTMLInputElement).checked,
                })
              }
            />
            Только для staff
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: (e.target as HTMLInputElement).checked,
                })
              }
            />
            Активен
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={
              !form.name.trim() ||
              !form.discount_value ||
              saveMutation.isPending
            }
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function GeneratorDialog({
  voucher,
  onClose,
}: {
  voucher: Voucher;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [count, setCount] = useState("10");
  const [pattern, setPattern] = useState("RICH-XXXX");

  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/vouchers/${voucher.id}/codes/generate`,
        {
          count: parseInt(count, 10) || 1,
          pattern: pattern.trim() || "XXXX",
        },
      );
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(`Сгенерировано ${data.length} кодов`);
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка генерации"),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate codes для «{voucher.name}»</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Сколько кодов</Label>
            <Input
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              min={1}
              max={10000}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Pattern</Label>
            <Input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="RICH-XXXX"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              <code>X</code> — случайный A-Z или 0-9 ·{" "}
              <code>#</code> — только цифра · <code>?</code> — только буква ·
              остальное as-is
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
